import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import groupBy from 'lodash/groupBy';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';

import CircleIcon from '@mui/icons-material/Circle';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { green, grey, red } from '@mui/material/colors';

import Image from '~/components/Common/Image';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';
import { CruiseShip, CruiseShipAmenity } from '~/components/Cruises/pages/Ships/types';

import shipService from '~/services/cruises/ShipService';

interface Props {
  ship: CruiseShip;
  onUpdateShip: () => void;
}

const CrawlerStatusColors = {
  crawled: green[500],
  pending: grey[500],
  error: red[500],
};

function ShipAmenities({ ship, onUpdateShip }: Props) {
  const [amenities, setAmenities] = useState<CruiseShipAmenity[]>([]);
  const [fetching, setFetching] = useState(false);
  const [selectedAmenityCategory, setSelectedAmenityCategory] = useState<string | null>(null);
  const [isCrawlerAmenitiesCurated, setIsCrawlerAmenitiesCurated] = useState<boolean>(ship.isCrawlerAmenitiesCurated);
  const { shipId } = useParams<{ shipId: string }>();

  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const amenitiesByCategory = useMemo(() => {
    return groupBy(amenities || [], (amenity) => amenity.category);
  }, [amenities]);

  const categories = useMemo(() => Object.keys(amenitiesByCategory), [amenitiesByCategory]);

  const selectedAmenities = useMemo(() => {
    return amenitiesByCategory[selectedAmenityCategory] || [];
  }, [amenitiesByCategory, selectedAmenityCategory]);

  const onCurateAmenities = useCallback(
    async (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      try {
        event.preventDefault();
        const title = checked ? 'Approve Amenities?' : 'Disapprove Amenities?';

        const confirmed = await showConfirmDialog({
          title,
          description: 'Approved amenities will be displayed on the Customer Portal',
        });

        if (!confirmed) {
          return false;
        }

        await shipService.updateShip(shipId, ship.cruiseCriticId, checked);
        setIsCrawlerAmenitiesCurated(checked);
        enqueueSnackbar('Amenities status updated', { variant: 'success' });

        onUpdateShip();
      } catch (error) {
        enqueueSnackbar('Error updating amenities status', { variant: 'error' });
      }
    },
    [isCrawlerAmenitiesCurated],
  );

  const hasAmenities = useMemo(() => !!amenities.length, [amenities]);

  useEffect(() => {
    const fetchAmenities = async () => {
      if (!shipId || fetching) return;

      setFetching(true);

      const response = await shipService.getShipAmenities(shipId);
      setAmenities(response.result);

      setFetching(false);
    };

    fetchAmenities();
  }, [shipId, setAmenities, setFetching]);

  useEffect(() => {
    if (categories.length) {
      setSelectedAmenityCategory(categories[0]);
    }
  }, [categories]);

  return (
    <Stack divider={<Divider orientation="horizontal" flexItem />}>
      <Stack sx={{ bgcolor: 'grey.50' }} p={2} spacing={1}>
        <Typography variant="body1" alignItems="center" display="flex" paddingLeft={1} gap={1}>
          <CircleIcon sx={{ color: CrawlerStatusColors[ship.crawlerAmenitiesStatus] }} />
          <b>Crawler status:</b>
          {ship.crawlerAmenitiesStatus}
        </Typography>

        {hasAmenities && (
          <FormControlLabel
            label="Approve amenities"
            control={
              <Checkbox
                checked={isCrawlerAmenitiesCurated}
                onChange={onCurateAmenities}
                inputProps={{ 'aria-label': 'controlled' }}
              />
            }
          />
        )}
      </Stack>

      {hasAmenities && (
        <Box sx={{ bgcolor: 'grey.100' }}>
          <Tabs value={selectedAmenityCategory} onChange={(_, value) => setSelectedAmenityCategory(value)}>
            {categories.map((category) => (
              <Tab key={category} label={category} value={category} />
            ))}
          </Tabs>
        </Box>
      )}

      <Box p={2}>
        <List>
          {!fetching &&
            selectedAmenities.map((amenity) => (
              <>
                <ListItem key={amenity.id} sx={{ gap: 2 }}>
                  <Image publicId={amenity.imageId} width={250} />
                  <ListItemText primary={amenity.title} secondary={amenity.description} />
                </ListItem>
                <Divider variant="inset" component="li" />
              </>
            ))}

          {!amenities.length && !fetching && (
            <Box display="flex" justifyContent="center">
              <Typography color="textSecondary">No amenities found</Typography>
            </Box>
          )}

          {fetching && <Spinner size={24} />}
        </List>
      </Box>
    </Stack>
  );
}

export default ShipAmenities;
